import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardBody,
  Collapse,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Row,
  Col,
  Spinner
} from 'reactstrap';
import {
  Circle,
  Copy,
  Battery,
  Thermometer,
  HardDrive,
  AlertTriangle,
  Clock,
  Edit3,
  RefreshCw,
  Activity,
  Heart,
  Smartphone,
  Camera as CameraIcon,
  Bookmark
} from 'lucide-react';
import axios from 'axios';
import DeviceDetailsTimeline from './DeviceDetailsTimeline';

// Style constants
const styles = {
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e3e6f0',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px'
  },
  recordingId: {
    background: '#f8fafc',
    borderBottom: '1px solid rgba(0,0,0,0.05)'
  },
  mobileDevice: {
    background: '#f8f9fe',
    borderLeft: '4px solid #4e73df',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  cameraDevice: {
    background: '#f7fcf9',
    borderLeft: '4px solid #1cc88a',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  bloodPressure: {
    background: '#fff8f8',
    borderLeft: '4px solid rgb(225, 109, 99)',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  annotatedBp: {
    background: '#fff8f8',
    borderLeft: '4px solid rgb(189, 47, 35)',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  bloodSugar: {
    background: '#f5f8ff',
    borderLeft: '4px solid #4e73df',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  notes: {
    container: {
      background: '#fafbfc',
      borderRadius: '8px'
    },
    note: {
      background: '#ffffff',
      border: '1px solid #e3e6f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }
  },
  button: {
    primary: {
      background: '#4e73df',
      color: '#ffffff',
      border: 'none',
      boxShadow: '0 2px 4px rgba(78,115,223,0.25)'
    }
  }
};


const RecordingCard = ({ recording, baseUrl, onNoteAdded }) => {
  // State declarations
  const [isOpen, setIsOpen] = useState(false);
  const [copiedItems, setCopiedItems] = useState({});
  const [status, setStatus] = useState('');
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [bloodSugars, setBloodSugars] = useState([]);
  const [bloodPressures, setBloodPressures] = useState([]);
  const [annotatedBloodPressures, setAnnotatedBloodPressures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState(recording.notes || []);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [hasRequestFailed, setHasRequestFailed] = useState(false);
  // Set initial status
  useEffect(() => {
    if (recording.recordingCanceled) {
      setStatus('CANCELLED');
    } else if (recording.recordingEnded !== 0) {
      setStatus('COMPLETED');
    } else {
      const allDevicesCancelled = recording.recordingDevices.mobileDevices.every(device => device.cancelled);
      if (allDevicesCancelled) {
        setStatus('MOBILE_CANCEL');
      } else {
        const now = Math.floor(Date.now() / 1000);
        const thirtyMinutesAgo = now - 30 * 60;
        const isActiveRecording = recording.recordingStarted > thirtyMinutesAgo;
        setStatus(isActiveRecording ? 'RECORDING' : 'ISSUE');
      }
    }
  }, [recording]);

  const fetchDeviceDetails = useCallback(async () => {
    if (!isLoading) {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.post(`${baseUrl}web/patientRecordingDetails`, {
          recordingId: recording.recordingId
        });
        console.log(response.data.success);
        if (response.data.success === false) {
          console.error('Error fetching device details:', error);
          setError(!response.data.message ? response.data.message : 'Failed to load device details. Please try again.');
          setHasRequestFailed(true);
        } else {

          setDeviceDetails(response.data.recordingDetails);
          setBloodSugars(response.data.bloodSugars);
          setAnnotatedBloodPressures(response.data.annotatedBloodPressures);
          setBloodPressures(response.data.bloodPressures);
          setHasRequestFailed(false);
        }
      } catch (error) {
        console.error('Error fetching device details:', error);
        setError('Failed to load device details. Please try again.');
        setHasRequestFailed(true);
      } finally {
        setIsLoading(false);
      }
    }
  }, [recording.recordingId, baseUrl, isLoading]);

  const handleRetry = () => {
    setHasRequestFailed(false);
    fetchDeviceDetails();
  };

  useEffect(() => {
    if (isOpen && !deviceDetails && !hasRequestFailed) {
      fetchDeviceDetails();
    }
  }, [isOpen, deviceDetails, fetchDeviceDetails, hasRequestFailed]);

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const copyToClipboard = useCallback((text, itemId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItems(prev => ({ ...prev, [itemId]: true }));
      setTimeout(() => {
        setCopiedItems(prev => ({ ...prev, [itemId]: false }));
      }, 2000);
    });
  }, []);

  const handleAddNote = async () => {
    if (newNote.trim()) {
      try {
        const response = await axios.post(`${baseUrl}web/patientAddNote`, {
          recordingId: recording.recordingId,
          note: newNote
        });
        if (response.data.success) {
          const updatedNotes = [...notes, { note: newNote, timestamp: Date.now() / 1000 }];
          setNotes(updatedNotes);
          onNoteAdded(recording.recordingId, updatedNotes);
          setNewNote('');
          setIsAddNoteModalOpen(false);
        }
      } catch (error) {
        console.error('Error adding note:', error);
      }
    }
  };

  const renderStatus = () => {
    const statusStyles = {
      RECORDING: {
        className: 'bg-success-subtle text-success',
        icon: <Circle size={12} className="me-1" fill="currentColor" />,
        text: 'Recording',
        style: {
          borderRight: '2px solid #198754',
          fontWeight: 500
        }
      },
      COMPLETED: {
        className: 'bg-primary-subtle text-primary',
        icon: <Activity size={14} className="me-1" />,
        text: 'Completed',
        style: {
          borderRight: '2px solid #0d6efd',
          fontWeight: 500
        }
      },
      CANCELLED: {
        className: 'bg-danger-subtle text-danger',
        icon: <AlertTriangle size={14} className="me-1" />,
        text: 'Cancelled',
        style: {
          borderRight: '2px solid #dc3545',
          fontWeight: 500
        }
      },
      MOBILE_CANCEL: {
        className: 'bg-warning-subtle text-warning',
        icon: <AlertTriangle size={14} className="me-1" />,
        text: 'Mobile Cancel',
        style: {
          borderRight: '2px solid #ffc107',
          fontWeight: 500
        }
      },
      ISSUE: {
        className: 'bg-warning-subtle text-warning',
        icon: <AlertTriangle size={14} className="me-1" />,
        text: 'Issue',
        style: {
          borderRight: '2px solid #ffc107',
          fontWeight: 500
        }
      }
    };

    const config = statusStyles[status] || statusStyles.COMPLETED;

    return (
      <div
        className={`d-inline-flex align-items-center px-2 py-1 rounded ${config.className}`}
        style={config.style}
      >
        {config.icon}
        <span>{config.text}</span>
      </div>
    );
  };
  // Add this helper function in RecordingCard.js
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '';

    const durationInSeconds = endTime - startTime;
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    let duration = '';
    if (hours > 0) duration += `${hours}h `;
    if (minutes > 0 || hours > 0) duration += `${minutes}m `;
    duration += `${seconds}s`;

    return duration;
  };
  const renderDeviceInfo = (device, index, type = 'mobile') => {
    const deviceDetail = deviceDetails?.find(d => d.deviceId === device.deviceId);
    const deviceDetailData = deviceDetail?.deviceDetails || [];

    const isRecording = !device.cancelled &&
      !device.dropped &&
      device.recordingStarted > (Date.now() / 1000 - 30 * 60) &&
      device.recordingEnded === 0;

    return (
      <Card key={index} className="mb-3" style={type === 'mobile' ? styles.mobileDevice : styles.cameraDevice}>
        <CardBody>
          <Row>
            <Col md={8}>
              <div className="d-flex align-items-center mb-2">
                {type === 'mobile' ?
                  <Smartphone size={20} className="text-primary me-2" /> :
                  <CameraIcon size={20} className="text-success me-2" />
                }
                <h6 className="mb-0 fw-bold">
                  {device.deviceName || `Device ${index + 1}`}
                </h6>
                {isRecording && (
                  <div className="ms-2 d-inline-flex align-items-center px-2 py-1 bg-success-subtle text-success rounded">
                    <Circle size={8} className="me-1" fill="currentColor" />
                    Recording
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <code className="bg-white px-2 py-1 rounded small">
                  {device.deviceId}
                </code>
                <Button
                  color="link"
                  className="p-0 ms-2"
                  onClick={() => copyToClipboard(device.deviceId, `device_${index}`)}
                >
                  <Copy size={14} />
                </Button>
                {copiedItems[`device_${index}`] && (
                  <small className="text-success ms-1">Copied!</small>
                )}
              </div>
            </Col>
            <Col md={4} className="text-md-end">
              {type === 'mobile' && (
                <>
                  <div
                    className={`d-inline-flex align-items-center me-2 px-2 py-1 rounded ${device.camera === 'Front'
                      ? 'bg-info-subtle text-info'
                      : 'bg-secondary-subtle text-secondary'
                      }`}
                  >
                    {device.camera} Camera
                  </div>
                  <div
                    className={`d-inline-flex align-items-center px-2 py-1 rounded ${device.bleEnabled
                      ? 'bg-success-subtle text-success'
                      : 'bg-warning-subtle text-warning'
                      }`}
                  >
                    Bluetooth {device.bleEnabled ? "On" : "Off"}
                  </div>
                </>
              )}
            </Col>
          </Row>

          {deviceDetailData.length > 0 && !device.cancelled && !device.dropped && (
            <div className="mt-3">
              <DeviceDetailsTimeline
                deviceDetails={deviceDetailData}
                recordingStart={recording.recordingStarted}
                recordingEnd={recording.recordingEnded}
                deviceId={device.deviceId}
                recordingId={recording.recordingId}
                deviceRecordingStart={device.recordingStarted}
                deviceRecordingEnd={device.recordingEnded}
              />
            </div>
          )}

          {(device.cancelled || device.dropped || device.failedReason) && (
            <div className="mt-3">
              {device.cancelled && (
                <div className="d-inline-flex align-items-center me-2 px-2 py-1 bg-danger-subtle text-danger rounded">
                  Cancelled
                </div>
              )}
              {device.dropped && (
                <div className="d-inline-flex align-items-center me-2 px-2 py-1 bg-warning-subtle text-warning rounded">
                  Dropped
                </div>
              )}
              {device.failedReason && (
                <div className="d-inline-flex align-items-center px-2 py-1 bg-danger-subtle text-danger rounded">
                  {device.failedReason}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  const groupAnnotatedBloodPressures = (annotatedBPs) => {
    return _.groupBy(annotatedBPs, 'annotateSessionId');
  };
  
  const renderAnnotatedBloodPressures = () => {
    if (!annotatedBloodPressures?.length) return null;
  
    const groupedSessions = groupAnnotatedBloodPressures(annotatedBloodPressures);
  
    return Object.entries(groupedSessions).map(([sessionId, readings]) => (
      <Card key={sessionId} className="mb-3" style={styles.annotatedBp}>
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <Heart size={18} className="text-danger me-2" />
              <h6 className="mb-0 fw-bold">Annotated Blood Pressure</h6>
            </div>
            <div className={`d-inline-flex align-items-center px-2 py-1 rounded ${readings[0].accepted 
                      ? 'bg-success-subtle text-success'
                      : 'bg-warning-subtle text-warning'
                      }`}>
              {readings[0].accepted ? 'Accepted' : 'Not Accepted'}
            </div>
          </div>
          <Row>
            {readings.map((reading, index) => (
              <Col key={index} md={6}>
                <div className="mb-2 bg-white border rounded p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 fw-bold text-danger">
                      {reading.systolic}/{reading.diastolic}
                      <small className="text-muted ms-2 fs-6 fw-normal">mmHg</small>
                    </h4>
                    <small className="text-muted">
                      {formatDate(reading.createdAt)}
                    </small>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Annotator: {reading.annotaterId}</small>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>
    ));
  };
  
  const renderHealthMetrics = () => {
    const hasValidBloodSugar = bloodSugars?.length > 0 &&
      bloodSugars[0]?.didBloodSugarMeasurementHappened === 'yes';
  
    if (!bloodPressures?.length && !hasValidBloodSugar && !annotatedBloodPressures?.length) {
      return null;
    }
    
    return (
      <div className="mt-4">
        <h6 className="mb-3 d-flex align-items-center">
          <Activity size={18} className="me-2" />
          Health Metrics
        </h6>
        
        {/* Annotated Blood Pressures */}
        {renderAnnotatedBloodPressures()}
        
        <Row>
          {bloodPressures?.length > 0 && (
            <Col md={hasValidBloodSugar ? 6 : 12} className="mb-3">
              <Card className="h-100" style={styles.bloodPressure}>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <Heart size={18} className="text-danger me-2" />
                    <h6 className="mb-0 fw-bold">Blood Pressure</h6>
                  </div>
                  {bloodPressures.map((bp, index) => (
                    <div key={index} className="mb-2 bg-white border rounded p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold text-danger">
                          {bp.systolic}/{bp.diastolic}
                          <small className="text-muted ms-2 fs-6 fw-normal">mmHg</small>
                        </h4>
                        <small className="text-muted">
                          {formatDate(bp.createdAt)}
                        </small>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          )}
  
          {hasValidBloodSugar && (
            <Col md={bloodPressures?.length > 0 ? 6 : 12} className="mb-3">
              <Card className="h-100" style={styles.bloodSugar}>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <Activity size={18} className="text-primary me-2" />
                    <h6 className="mb-0 fw-bold">Blood Sugar</h6>
                  </div>
                  {bloodSugars.map((bs, index) => (
                    <div key={index} className="mb-2 bg-white border rounded p-3">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h4 className="mb-2 fw-bold text-primary">
                            {bs.bloodSugar}
                            <small className="text-muted ms-2 fs-6 fw-normal">mg/dL</small>
                          </h4>
                          {bs.lastMealTime && (
                            <div className="d-flex align-items-center">
                              <div className="badge bg-info-subtle text-info">
                                Last meal: Between {bs.lastMealTime.split('-')[0]}-{bs.lastMealTime.split('-')[1]} hours ago
                              </div>
                            </div>
                          )}
                          {bs.didBloodSugarMeasurementHappened === 'no' && (
                            <div className="badge bg-warning-subtle text-warning mt-2">
                              Measurement not taken
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          {formatDate(bs.createdAt)}
                        </small>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };
  return (
    <Card className="mb-4 border">
      <CardBody className="p-0">
        {/* Header Section */}
        <div
          className="p-3"
          style={styles.header}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }}>
            <div className="d-flex align-items-center">
              <div className="me-4">
                <div className="d-flex align-items-center mb-1">
                  <h5 className="mb-0">{formatDate(recording.createdAt)}</h5>
                  <div className="ms-3">
                    {renderStatus()}
                  </div>
                  {recording.recordingStarted !== 0 && recording.recordingEnded !== 0 && !recording.recordingCanceled && (
                    <div className="ms-3 d-inline-flex align-items-center px-2 py-1 bg-light text-secondary rounded">
                      <Clock size={14} className="me-1" />
                      {formatDuration(recording.recordingStarted, recording.recordingEnded)}
                    </div>
                  )}
                </div>

              </div>
            </div>

            <div className="d-flex align-items-center">
              <div className="d-inline-flex align-items-center me-2 px-2 py-1 bg-primary-subtle text-primary rounded">
                <Smartphone size={14} className="me-1" />
                <span className="fw-medium">{recording.recordingDevices.mobileDevices.length}</span>
              </div>
              <div className="d-inline-flex align-items-center me-3 px-2 py-1 bg-success-subtle text-success rounded">
                <CameraIcon size={14} className="me-1" />
                <span className="fw-medium">{recording.recordingDevices.cameraDevices.length}</span>
              </div>
              <span className="fs-5">{isOpen ? '▼' : '▲'}</span>
            </div>
          </div>
        </div>

        {/* Collapsible Content */}
        <Collapse isOpen={isOpen}>
          {/* Recording ID and Refresh Section */}
          <div className="p-3" style={styles.recordingId}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <code className="bg-white px-3 py-2 rounded">
                  {recording.recordingId}
                </code>
                <Button
                  color="link"
                  className="p-0 ms-2"
                  onClick={() => copyToClipboard(recording.recordingId, 'recordingId')}
                >
                  <Copy size={14} />
                </Button>
                {copiedItems['recordingId'] && (
                  <small className="text-success ms-2">Copied!</small>
                )}
              </div>

              <Button
                color="primary"
                size="sm"
                className="px-3 py-2"
                style={styles.button.primary}
                onClick={handleRetry}
                disabled={isLoading}
              >
                <RefreshCw size={14} className="me-2" />
                {hasRequestFailed ? 'Retry' : 'Refresh Details'}
              </Button>
            </div>
          </div>

          <div className="p-3">
            {/* Loading and Error States */}
            {isLoading && (
              <div className="text-center my-4">
                <Spinner color="primary" size="sm" />
              </div>
            )}
            {error && (
              <div style={styles.errorAlert} className="d-flex align-items-center">
                <AlertTriangle size={18} className="text-danger me-2" />
                <div className="flex-grow-1">
                  <div className="text-danger fw-medium">Error loading details</div>
                  <div className="text-muted small">Please try refreshing the details.</div>
                </div>
              </div>
            )}

            {/* Devices Section */}
            {!error && deviceDetails && (
              <div>
                <h6 className="mb-3 d-flex align-items-center">
                  <Smartphone size={18} className="me-2" />
                  Mobile Devices
                </h6>
                {recording.recordingDevices.mobileDevices.map((device, index) =>
                  renderDeviceInfo(device, index, 'mobile')
                )}

                {recording.recordingDevices.cameraDevices.length > 0 && (
                  <div className="mt-4">
                    <h6 className="mb-3 d-flex align-items-center">
                      <CameraIcon size={18} className="me-2" />
                      Camera Devices
                    </h6>
                    {recording.recordingDevices.cameraDevices.map((device, index) =>
                      renderDeviceInfo(device, index, 'camera')
                    )}
                  </div>
                )}
              </div>
            )}


            {/* Health Metrics */}
            {renderHealthMetrics()}

            {/* Notes Section */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 d-flex align-items-center">
                  <Edit3 size={18} className="me-2" />
                  Notes
                </h6>
                <Button
                  color="primary"
                  size="sm"
                  className="px-3 py-2"
                  style={styles.button.primary}
                  onClick={() => setIsAddNoteModalOpen(true)}
                >
                  <Edit3 size={14} className="me-2" />
                  Add Note
                </Button>
              </div>
              {notes?.length > 0 ? (
                <div style={styles.notes.container} className="border rounded p-3">
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notes.map((note, index) => (
                      <div key={index} style={styles.notes.note} className="mb-2 rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted fw-medium">
                            {formatDate(note.timestamp)}
                          </small>
                        </div>
                        <p className="mb-0">{note.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center border rounded p-4" style={styles.notes.container}>
                  <Edit3 size={24} className="text-muted mb-2" />
                  <p className="text-muted mb-0">No notes available</p>
                </div>
              )}
            </div>
          </div>
        </Collapse>

        {/* Add Note Modal */}
        <Modal isOpen={isAddNoteModalOpen} toggle={() => setIsAddNoteModalOpen(!isAddNoteModalOpen)}>
          <ModalHeader toggle={() => setIsAddNoteModalOpen(!isAddNoteModalOpen)}>
            Add Note
          </ModalHeader>
          <ModalBody>
            <Input
              type="textarea"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setIsAddNoteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              style={styles.button.primary}
              onClick={handleAddNote}
            >
              Add Note
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default RecordingCard;